import { defineStore } from 'pinia';
import { EPaymentsType, type IPaymentData, type IPaymentStrapiResponse } from '~/types/payments';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import { findIfPassesByCountry, findIfPassesByGroups } from '~/utils/strapiComponents/filters';
import { useStoreRoot } from '../abstract/root';
import { strapi } from '../transport/strapi';

export const useStorePaymentsData = defineStore('paymentsData', {
  state: () => ({
    activePayments: [] as IPaymentData[],
    requestPromise: null as Promise<IPaymentStrapiResponse> | null,
  }),

  getters: {
    personalPayments(): IPaymentData[] {
      return this.activePayments.filter((p) => {
        const isAllowedByCountry = findIfPassesByCountry(
          p.includedCountries || '',
          p.excludedCountries || ''
        );

        if (!isAllowedByCountry) {
          return false;
        }

        const isAllowedByGroups = findIfPassesByGroups(
          p.includedGroups || '',
          p.excludedGroups || ''
        );

        if (!isAllowedByGroups) {
          return false;
        }

        return true;
      });
    },
    deposit(): IPaymentData[] {
      return this.personalPayments.filter((payment) => payment.type === EPaymentsType.deposit);
    },
    withdrawal(): IPaymentData[] {
      return this.personalPayments.filter((payment) => payment.type === EPaymentsType.withdrawal);
    },
  },

  actions: {
    async loadPaymentsData(): Promise<boolean> {
      const storeRoot = useStoreRoot();

      if (!this.requestPromise) {
        const populateString = createPopulateString(['logo']);

        this.requestPromise = strapi()
          .get('payment-data', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData<IPaymentData>(response.data).filter((collection) => {
          if (storeRoot.isEstonianLicense) {
            return collection.showForEstonia;
          }
          return true;
        });

        const filteredState = this.activePayments.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.activePayments = [...filteredState, ...parsedData].sort(
          (a, b) => a.position - b.position
        );

        return true;
      } else {
        return false;
      }
    },
  },
});
